import React, { useEffect, useState, useCallback } from 'react';
import { X, Check, Users, AlertCircle, Clock } from 'lucide-react';

const MultiPatientConfirmDialog = ({ 
  isOpen, 
  onClose, 
  detectionInfo, 
  onConfirm, 
  onDecline 
}) => {
  const [timeLeft, setTimeLeft] = useState(20);

  // Create wrapper functions to close dialog before calling handlers
  const handleConfirm = useCallback(() => {
    // Close dialog immediately
    onClose();
    // Then trigger confirm action
    onConfirm();
  }, [onClose, onConfirm]);

  const handleDecline = useCallback(() => {
    // Close dialog immediately
    onClose();
    // Then trigger decline action
    onDecline();
  }, [onClose, onDecline]);

  // Auto-decline after 20 seconds
  useEffect(() => {
    if (!isOpen) {
      setTimeLeft(20);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          // Time's up - default to single patient (decline)
          handleDecline();
          return 20;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, handleDecline]);

  // Early return after all hooks are called
  if (!isOpen) return null;

  const patientList = detectionInfo?.potential_patients || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold flex items-center">
              <Users className="w-5 h-5 mr-2 text-teal-600" />
              Multiple Patients Detected
            </h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-blue-700 mt-0.5 mr-2 flex-shrink-0" />
              <p className="text-blue-700 text-sm">
                We've detected that you might be discussing multiple patients in this recording.
                Would you like to create separate patient records and notes for each patient?
              </p>
            </div>
          </div>
          
          {detectionInfo?.evidence && (
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-medium">Evidence:</span> {detectionInfo.evidence}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Confidence:</span> {detectionInfo.confidence}%
              </p>
            </div>
          )}
          
          <div className="space-y-4 max-h-52 overflow-y-auto">
            {patientList.map((patient, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-3">
                <h3 className="font-semibold mb-1 text-teal-700">
                  {patient.name !== "Unknown" ? patient.name : `Patient ${index + 1}`}
                </h3>
                
                {patient.conditions && patient.conditions.length > 0 && (
                  <div className="mb-1">
                    <p className="text-xs font-medium text-gray-500">Conditions:</p>
                    <p className="text-sm text-gray-700">
                      {patient.conditions.join(', ')}
                    </p>
                  </div>
                )}
                
                {patient.sample_text && (
                  <div>
                    <p className="text-xs font-medium text-gray-500">Sample:</p>
                    <p className="text-sm text-gray-700 italic">
                      "{patient.sample_text}"
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="mt-4 flex items-center justify-center text-sm text-gray-500">
            <Clock className="w-4 h-4 mr-1" />
            <span>Defaulting to single patient in {timeLeft} seconds</span>
          </div>
          
          <div className="mt-6 flex justify-end space-x-4">
            <button
              onClick={handleDecline}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              No, Just One Patient
            </button>
            <button
              onClick={handleConfirm}
              className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 flex items-center"
            >
              <Check className="w-4 h-4 mr-2" />
              Yes, Create Multiple Patients
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiPatientConfirmDialog;